import { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, Plus, Upload, Trash2, Pencil, X, Save, Users, Wallet, HardHat,
  Building2, ShieldCheck, Wrench, TrendingUp, Landmark, LayoutDashboard,
  Sparkles, ArrowRightLeft, Landmark as Bank, FileDown, AlertTriangle, Send,
  CheckSquare, Square, ExternalLink, RefreshCw, BadgeCheck, GraduationCap,
  FileText, CalendarClock, Check, Images, Boxes,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import PortalInvoices from "./PortalInvoices";
import PortalBillPay from "./PortalBillPay";
import JobProgress from "./JobProgress";
import InventoryBoard from "./InventoryBoard";

const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const inputCls = "w-full min-w-0 box-border rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none";

const ICONS = {
  users: Users, wallet: Wallet, hardhat: HardHat, building: Building2,
  shield: ShieldCheck, wrench: Wrench, trending: TrendingUp, landmark: Landmark,
};
// Sub-nav order: Overview + Labor (derived) inserted after payroll.
const NAV_ORDER = ["overview", "jobs", "inventory", "employees", "onboarding", "payroll", "labor", "overhead", "insurance", "equipment", "revenue", "taxes"];

export default function PortalOperations({ clientId = null, admin = false, money: moneyProp = money }) {
  const base = admin && clientId ? `/admin/operations/${encodeURIComponent(clientId)}` : "/portal/operations";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState("overview");
  const [form, setForm] = useState(null);        // {entity, values}
  const [moveFor, setMoveFor] = useState(null);   // {entity, row}
  const [progressJob, setProgressJob] = useState(null);
  const [smartBusy, setSmartBusy] = useState(false);
  const [bankBusy, setBankBusy] = useState(false);
  const [taxBusy, setTaxBusy] = useState(false);
  const [cpaModal, setCpaModal] = useState(false);
  const [cpaBusy, setCpaBusy] = useState(false);
  const [cpaEmail, setCpaEmail] = useState("");
  const [cpaName, setCpaName] = useState("");
  const [threshDraft, setThreshDraft] = useState(null);
  const fileRef = useRef(null);
  const smartRef = useRef(null);
  const importEntityRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(base); setData(data); }
    catch { toast.error("Could not load operations."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [base]); // eslint-disable-line react-hooks/exhaustive-deps

  const entitiesByKey = useMemo(() => {
    const m = {};
    (data?.entities || []).forEach((e) => { m[e.key] = e; });
    return m;
  }, [data]);

  const refOptions = (refKey) => (data?.data?.[refKey] || []).map((r) => ({ id: r.id, name: r.name }));

  const blankValues = (spec) => {
    const v = {};
    spec.fields.forEach((f) => {
      if (f.type === "bool") v[f.key] = false;
      else if (f.type === "select") v[f.key] = f.default || f.options[0];
      else if (f.type === "num") v[f.key] = "";
      else if (f.type === "date") v[f.key] = f.key.includes("date") ? new Date().toISOString().slice(0, 10) : "";
      else v[f.key] = "";
    });
    return v;
  };

  const openAdd = (entity) => setForm({ entity, values: blankValues(entitiesByKey[entity]) });
  const openEdit = (entity, row) => {
    const spec = entitiesByKey[entity];
    const v = {};
    spec.fields.forEach((f) => {
      const cur = row[f.key];
      if (f.type === "num") v[f.key] = cur == null ? "" : String(cur);
      else if (f.type === "bool") v[f.key] = !!cur;
      else v[f.key] = cur == null ? "" : cur;
    });
    setForm({ entity, id: row.id, values: v });
  };

  const save = async () => {
    const spec = entitiesByKey[form.entity];
    const body = {};
    spec.fields.forEach((f) => {
      const val = form.values[f.key];
      body[f.key] = f.type === "num" ? (Number(val) || 0) : val;
    });
    setBusy(true);
    try {
      if (form.id) await api.put(`${base}/${form.entity}/${form.id}`, body);
      else await api.post(`${base}/${form.entity}`, body);
      setForm(null); await load();
      toast.success(form.id ? "Saved." : `${spec.singular} added.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const del = async (entity, id) => {
    if (!window.confirm("Remove this record?")) return;
    try { await api.delete(`${base}/${entity}/${id}`); await load(); toast.success("Removed."); }
    catch { toast.error("Could not remove."); }
  };

  const togglePaid = async (entity, row) => {
    try {
      await api.put(`${base}/${entity}/${row.id}`, { ...row, paid: !row.paid });
      await load();
    } catch { toast.error("Could not update."); }
  };

  const triggerImport = (entity) => { importEntityRef.current = entity; fileRef.current?.click(); };
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (file) e.target.value = "";
    if (!file || !importEntityRef.current) return;
    const entity = importEntityRef.current;
    setBusy(true); toast.message("Reading your file with AI…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`${base}/${entity}/import`, fd);
      await load(); toast.success(`Added ${data.added} record${data.added === 1 ? "" : "s"}.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read that file."); }
    finally { setBusy(false); }
  };

  const onSmartUpload = async (e) => {
    const file = e.target.files?.[0]; if (file) e.target.value = "";
    if (!file) return;
    setSmartBusy(true); toast.message("Scanning & auto-sorting your document…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`${base}/smart-import`, fd);
      await load();
      const summary = Object.entries(data.counts || {}).map(([k, v]) => `${v} ${entitiesByKey[k]?.label || k}`).join(", ");
      toast.success(`Sorted ${data.added} record${data.added === 1 ? "" : "s"} → ${summary}`, { duration: 6000 });
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read that document."); }
    finally { setSmartBusy(false); }
  };

  const move = async (entity, id, target) => {
    try {
      const { data } = await api.post(`${base}/${entity}/${id}/move`, { target });
      setMoveFor(null); await load(); toast.success(`Moved to ${data.moved_to}.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not move."); }
  };

  const bankSync = async () => {
    setBankBusy(true); toast.message("Pulling bank transactions & auto-sorting…");
    try {
      const { data } = await api.post(`${base}/bank-sync`);
      await load();
      if (!data.added) { toast.success(data.message || "No new transactions."); return; }
      const summary = Object.entries(data.counts || {}).map(([k, v]) => `${v} ${entitiesByKey[k]?.label || k}`).join(", ");
      toast.success(`Imported ${data.added} → ${summary}`, { duration: 6000 });
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (err?.response?.status === 409 && /connect a bank/i.test(detail)) {
        toast.message("No bank linked yet — opening the secure Stripe connect flow…");
        connectBank();
      } else {
        toast.error(detail || "Could not sync bank.");
      }
    } finally { setBankBusy(false); }
  };

  const [bankConnecting, setBankConnecting] = useState(false);
  const connectBank = async () => {
    setBankConnecting(true);
    try {
      const { data: res } = await api.post("/portal/banking/start");
      if (!res?.url) { toast.error("Stripe returned no link. Try again in a minute."); return; }
      const win = window.open(res.url, "_blank", "noopener");
      if (win) toast.success("Opening Stripe to securely link your bank…");
      else { try { await navigator.clipboard.writeText(res.url); } catch { /* ignore */ } toast.info("Popup blocked — link copied. Paste it in a new tab."); }
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        try {
          const { data: sync } = await api.post("/portal/banking/sync");
          if (sync?.linked) {
            clearInterval(iv);
            toast.success(`${sync.account?.brand || "Bank"} linked ✓ — you can now Sync bank and fund payouts.`);
            await load();
          }
        } catch { /* keep polling */ }
        if (tries >= 30) clearInterval(iv);
      }, 3000);
    } catch (e) {
      const d = e?.response?.data?.detail || "";
      if (/identity/i.test(d)) toast.error("Please verify your identity first (Business finances → Verify identity), then connect a bank.");
      else toast.error(d || "Bank connect unavailable.");
    } finally { setBankConnecting(false); }
  };

  const downloadTaxPack = async () => {
    setTaxBusy(true);
    try {
      const res = await api.get(`${base}/tax-pack.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "elevare-tax-pack.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the tax pack."); }
    finally { setTaxBusy(false); }
  };

  const saveThreshold = async () => {
    const v = Number(threshDraft);
    if (Number.isNaN(v)) { setThreshDraft(null); return; }
    try {
      await api.put(`${base}/settings`, { margin_threshold: v });
      setThreshDraft(null); await load(); toast.success("Alert threshold saved.");
    } catch { toast.error("Could not save threshold."); }
  };

  const empConnect = async (eid) => {
    try {
      const { data } = await api.post(`${base}/employees/${eid}/connect`);
      window.open(data.url, "_blank", "noopener");
      toast.message("Opened the secure Stripe onboarding link. After the employee finishes, click Refresh.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start onboarding."); }
  };
  const empRefresh = async (eid) => {
    try {
      const { data } = await api.get(`${base}/employees/${eid}/connect-status`);
      await load();
      toast.success(data.status === "ready" ? "Ready to pay — bank verified." : "Onboarding still in progress.");
    } catch { toast.error("Could not refresh status."); }
  };
  const empOnboard = async (eid, field, val) => {
    try { await api.put(`${base}/employees/${eid}/onboarding`, { [field]: val }); await load(); }
    catch { toast.error("Could not update onboarding."); }
  };
  const payRun = async (pid) => {
    if (!window.confirm("Send this payroll payment to the employee's bank now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/payroll/${pid}/pay`);
      await load();
      toast.success(`Payout started — in transit${data.emailed ? " (employee emailed)" : ""}. We'll mark it Paid once the bank confirms the deposit.`, { duration: 6000 });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send payout."); }
    finally { setBusy(false); }
  };
  const checkPayout = async (pid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/payroll/${pid}/payout-status`);
      await load();
      const labels = { in_transit: "still in transit", paid: "confirmed deposited ✓", failed: "failed", returned: "returned by the bank" };
      toast[data.status === "paid" ? "success" : "message"](data.note || `Payout ${labels[data.status] || data.status}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not check payout status."); }
    finally { setBusy(false); }
  };
  const [stripeChk, setStripeChk] = useState(null);
  const stripeCheck = async () => {
    setStripeChk({ loading: true });
    try {
      const { data } = await api.get(`${base}/stripe-check`);
      setStripeChk(data);
      toast[data.ok ? "success" : "message"](data.message);
    } catch (e) { setStripeChk(null); toast.error(e?.response?.data?.detail || "Could not check Stripe setup."); }
  };

  const downloadStub = async (pid, name) => {
    try {
      const res = await api.get(`${base}/payroll/${pid}/stub.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `pay-stub-${(name || "employee").toLowerCase().replace(/\s+/g, "-")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the pay stub."); }
  };
  const approveDraft = async (pid) => {
    try { await api.post(`${base}/payroll/${pid}/approve-draft`); await load(); toast.success("Draft approved."); }
    catch { toast.error("Could not approve draft."); }
  };

  const [sched, setSched] = useState(null);
  const [schedBusy, setSchedBusy] = useState(false);
  const loadSchedule = async () => {
    try { const { data } = await api.get(`${base}/payroll-schedule`); setSched(data); } catch { /* ignore */ }
  };
  useEffect(() => { if (section === "payroll" && !sched) loadSchedule(); /* eslint-disable-next-line */ }, [section]);
  const saveSchedule = async (patch) => {
    const next = { ...sched, ...patch };
    setSchedBusy(true);
    try {
      const { data } = await api.put(`${base}/payroll-schedule`, { enabled: next.enabled, cadence: next.cadence, anchor: next.anchor });
      setSched(data); toast.success("Payroll schedule saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save schedule."); }
    finally { setSchedBusy(false); }
  };
  const runSchedule = async () => {
    setSchedBusy(true);
    try {
      const { data } = await api.post(`${base}/payroll-schedule/run-now`);
      await load(); await loadSchedule();
      toast[data.drafted ? "success" : "message"](data.drafted ? `Drafted ${data.drafted} pay run${data.drafted === 1 ? "" : "s"} to review.` : (data.reason || "No pay runs due yet."));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate drafts."); }
    finally { setSchedBusy(false); }
  };

  const openCpaModal = () => {
    setCpaEmail(data?.settings?.cpa_email || "");
    setCpaName(data?.settings?.cpa_name || "");
    setCpaModal(true);
  };
  const sendToCpa = async () => {
    if (!cpaEmail.includes("@")) { toast.error("Enter a valid accountant email."); return; }
    setCpaBusy(true);
    try {
      await api.post(`${base}/tax-pack/email`, { cpa_email: cpaEmail.trim(), cpa_name: cpaName.trim() });
      setCpaModal(false); await load();
      toast.success(`Tax pack emailed to ${cpaEmail.trim()}.`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send the email."); }
    finally { setCpaBusy(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-[#1e3d3a]/60 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading operations…</div>;
  if (!data) return null;

  const roll = data.rollup || {};
  const displayName = (entity, row) => {
    const spec = entitiesByKey[entity];
    const first = spec.fields.find((f) => f.type === "str");
    return row[first?.key] || row.employee_name || row.name || "—";
  };

  return (
    <div data-testid="portal-operations" className="space-y-5">
      <input ref={fileRef} type="file" className="hidden" data-testid="ops-upload-input" onChange={onUpload}
        accept=".csv,.txt,.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls" />
      <input ref={smartRef} type="file" className="hidden" data-testid="ops-smart-input" onChange={onSmartUpload}
        accept=".csv,.txt,.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls" />

      {/* Smart upload — auto-scan & auto-sort into the right tabs */}
      <div data-testid="ops-smart-bar" className="rounded-2xl border border-[#c9a961]/40 bg-gradient-to-br from-[#faf6ea] to-white p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#c9a961]/20"><Sparkles className="h-4 w-4 text-[#a3823f]" /></div>
          <div>
            <div className="font-head text-base text-[#1e3d3a]">Smart upload</div>
            <p className="text-xs text-[#1e3d3a]/60 max-w-md">Drop in any document — payroll, invoices, insurance, receipts. AI reads it and files each record into the right tab automatically.</p>
          </div>
        </div>
        <button data-testid="ops-smart-upload-btn" disabled={smartBusy} onClick={() => smartRef.current?.click()}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50 transition-colors whitespace-nowrap">
          {smartBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {smartBusy ? "Sorting…" : "Upload & auto-sort"}
        </button>
        <button data-testid="ops-bank-connect-btn" disabled={bankConnecting} onClick={connectBank}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50 transition-colors whitespace-nowrap">
          {bankConnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Landmark className="h-4 w-4" />} Connect bank
        </button>
        <button data-testid="ops-bank-sync-btn" disabled={bankBusy} onClick={bankSync}
          className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#1e3d3a]/25 bg-white px-4 py-2 text-sm font-semibold text-[#1e3d3a] hover:border-[#c9a961] disabled:opacity-50 transition-colors whitespace-nowrap">
          {bankBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bank className="h-4 w-4" />} {bankBusy ? "Syncing…" : "Sync bank"}
        </button>
      </div>

      {/* Sub navigation */}
      <div className="flex flex-wrap gap-1.5" data-testid="ops-subnav">
        {NAV_ORDER.map((key) => {
          const meta = key === "overview" ? { label: "Overview", icon: null }
            : key === "labor" ? { label: "Labor cost", icon: "hardhat" }
            : key === "onboarding" ? { label: "Onboarding", icon: "shield" }
            : key === "inventory" ? { label: "Inventory", icon: "boxes" }
            : entitiesByKey[key];
          if (!meta) return null;
          const Icon = key === "overview" ? LayoutDashboard : key === "onboarding" ? BadgeCheck : key === "inventory" ? Boxes : ICONS[meta.icon];
          return (
            <button key={key} data-testid={`ops-nav-${key}`} onClick={() => setSection(key)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wide border transition-colors ${section === key ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/55 hover:text-[#1e3d3a]/85"}`}>
              {Icon && <Icon className="h-3.5 w-3.5" />} {meta.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW */}
      {section === "inventory" && <InventoryBoard />}

      {section === "overview" && (
        <div className="space-y-5" data-testid="ops-overview">
          <PortalInvoices />
          <PortalBillPay />
          {/* Job profit alert banner */}
          {(roll.at_risk_jobs || []).length > 0 && (
            <div data-testid="ops-profit-alert" className="rounded-2xl border border-[#b23a48]/30 bg-[#fbeced] p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-[#b23a48] shrink-0 mt-0.5" />
              <div className="text-sm text-[#7a1f2b]">
                <span className="font-semibold">{roll.at_risk_jobs.length} job{roll.at_risk_jobs.length === 1 ? "" : "s"} below your {roll.margin_threshold}% margin floor:</span>{" "}
                {roll.at_risk_jobs.map((j) => `${j.name} (${j.margin}%)`).join(", ")}.
                <span className="text-[#7a1f2b]/70"> The owner gets an email when a job first drops below this line.</span>
              </div>
            </div>
          )}

          {/* Controls: margin threshold + tax pack */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between rounded-2xl border border-[#1e3d3a]/12 bg-white px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-[#1e3d3a]/75">
              <AlertTriangle className="h-4 w-4 text-[#c9a961]" /> Alert me when a job's margin drops below
              <input data-testid="ops-margin-threshold" type="number" min="0" max="100"
                value={threshDraft ?? (data.settings?.margin_threshold ?? 15)}
                onChange={(e) => setThreshDraft(e.target.value)}
                className="w-16 rounded-lg border border-[#1e3d3a]/15 px-2 py-1 text-sm text-center" />%
              {threshDraft !== null && (
                <button data-testid="ops-margin-save" onClick={saveThreshold} className="ml-1 rounded-full bg-[#1e3d3a] px-3 py-1 text-xs font-semibold text-white hover:bg-[#2a534d]">Save</button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="ops-tax-pack-btn" disabled={taxBusy} onClick={downloadTaxPack}
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#1e3d3a]/25 px-4 py-2 text-sm font-semibold text-[#1e3d3a] hover:border-[#c9a961] disabled:opacity-50 transition-colors">
                {taxBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Tax Prep Pack (PDF)
              </button>
              <button data-testid="ops-cpa-email-btn" onClick={openCpaModal}
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] transition-colors">
                <Send className="h-4 w-4" /> Email to CPA
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {(roll.kpis || []).map((k) => (
              <div key={k.key} data-testid={`ops-kpi-${k.key}`} className="rounded-xl border border-[#1e3d3a]/12 bg-white px-4 py-3">
                <div className="text-[9px] uppercase tracking-wide text-[#1e3d3a]/45">{k.label}</div>
                <div className={`mt-1 font-head text-xl tabular-nums ${k.key === "job_profit" ? (k.value >= 0 ? "text-[#2f7d5b]" : "text-[#b23a48]") : "text-[#1e3d3a]"}`}>
                  {k.unit === "$" ? moneyProp(k.value) : k.unit === "%" ? `${k.value}%` : k.value}
                </div>
              </div>
            ))}
          </div>

          {/* Job profitability snapshot */}
          <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5" data-testid="ops-overview-jobs">
            <div className="flex items-center gap-2 text-[#1e3d3a] mb-3"><HardHat className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">Job profitability</h3></div>
            {(roll.jobs || []).length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead><tr className="text-[#1e3d3a]/45 border-b border-[#1e3d3a]/10 text-xs">
                    <th className="py-2 text-left font-medium">Job</th><th className="py-2 text-right font-medium">Contract</th>
                    <th className="py-2 text-right font-medium">Labor</th><th className="py-2 text-right font-medium">Overhead</th>
                    <th className="py-2 text-right font-medium">Total cost</th><th className="py-2 text-right font-medium">Profit</th><th className="py-2 text-right font-medium">Margin</th>
                  </tr></thead>
                  <tbody>
                    {roll.jobs.map((j) => (
                      <tr key={j.id} className="border-b border-[#1e3d3a]/6">
                        <td className="py-1.5 text-[#1e3d3a]/85">{j.name}{j.from_finance && <span className="ml-1.5 rounded-full bg-[#1e3d3a]/8 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-[#1e3d3a]/55">Finances</span>}</td>
                        <td className="py-1.5 text-right tabular-nums">{moneyProp(j.contract_value)}</td>
                        <td className="py-1.5 text-right tabular-nums text-[#1e3d3a]/70">{moneyProp(j.labor_cost)}</td>
                        <td className="py-1.5 text-right tabular-nums text-[#1e3d3a]/70">{moneyProp(j.overhead_alloc)}</td>
                        <td className="py-1.5 text-right tabular-nums">{moneyProp(j.total_cost)}</td>
                        <td className={`py-1.5 text-right tabular-nums font-medium ${j.profit >= 0 ? "text-[#2f7d5b]" : "text-[#b23a48]"}`}>{moneyProp(j.profit)}</td>
                        <td className={`py-1.5 text-right tabular-nums ${j.margin >= 0 ? "text-[#4d5a24]" : "text-[#b23a48]"}`}>{j.margin}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="text-sm text-[#1e3d3a]/50 py-6 text-center">Add jobs, employees & payroll to see live job profitability.</p>}
            <p className="mt-3 text-[10px] text-[#1e3d3a]/45">Labor rolls up from payroll assigned to each job. Overhead (rent, insurance, equipment upkeep) is allocated across active jobs by contract size.</p>
          </div>
        </div>
      )}

      {/* LABOR COST (derived) */}
      {section === "labor" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="ops-labor">
          <LaborTable title="Labor by job" rows={roll.labor_by_job} extra={roll.unassigned_labor ? [{ name: "Unassigned", amount: roll.unassigned_labor }] : []} money={moneyProp} />
          <LaborTable title="Labor by employee" rows={roll.labor_by_employee} money={moneyProp} />
        </div>
      )}

      {/* JOBS — costing cards */}
      {section === "jobs" && (
        <EntitySection
          entity="jobs" spec={entitiesByKey.jobs} rows={roll.jobs || data.data.jobs}
          onAdd={() => openAdd("jobs")} onImport={() => triggerImport("jobs")} busy={busy}
          render={(jobs) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {jobs.map((j) => (
                <div key={j.id} data-testid={`ops-job-${j.id}`} className="rounded-xl border border-[#1e3d3a]/12 bg-[#faf8f2] p-4">
                  <div className="flex items-start justify-between">
                    <div><div className="font-semibold text-[#1e3d3a]">{j.name}{j.added_by === "elevare" && <span className="ml-1.5 text-[9px] uppercase text-[#c9a961]">Elevare</span>}{j.at_risk && <span data-testid={`ops-job-risk-${j.id}`} className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-[#b23a48]/12 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-[#b23a48]"><AlertTriangle className="h-2.5 w-2.5" /> Low margin</span>}</div><div className="text-[10px] text-[#1e3d3a]/45">{j.client_name || "—"} · {j.status || "In progress"}</div></div>
                    <div className="flex gap-1">
                      <button data-testid={`ops-job-progress-${j.id}`} onClick={() => setProgressJob(j)} title="Progress / stages & photos" className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]"><Images className="h-3.5 w-3.5" /></button>
                      <button data-testid={`ops-job-move-${j.id}`} onClick={() => setMoveFor({ entity: "jobs", row: j })} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]"><ArrowRightLeft className="h-3.5 w-3.5" /></button>
                      <button data-testid={`ops-job-edit-${j.id}`} onClick={() => openEdit("jobs", j)} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`ops-job-del-${j.id}`} onClick={() => del("jobs", j.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#b23a48]"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  <div className="mt-3 h-2 w-full rounded-full bg-[#1e3d3a]/10 overflow-hidden"><div className="h-full rounded-full bg-[#c9a961]" style={{ width: `${Math.max(0, Math.min(100, j.pct_complete || 0))}%` }} /></div>
                  <div className="mt-1 text-[10px] text-[#1e3d3a]/45">{j.pct_complete || 0}% complete</div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                    <div><span className="text-[#1e3d3a]/40">Contract</span><div className="tabular-nums">{moneyProp(j.contract_value)}</div></div>
                    <div><span className="text-[#1e3d3a]/40">Labor</span><div className="tabular-nums">{moneyProp(j.labor_cost || 0)}</div></div>
                    <div><span className="text-[#1e3d3a]/40">Materials</span><div className="tabular-nums">{moneyProp(j.materials_cost || 0)}</div></div>
                    <div><span className="text-[#1e3d3a]/40">Overhead</span><div className="tabular-nums">{moneyProp(j.overhead_alloc || 0)}</div></div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#1e3d3a]/10 flex items-center justify-between">
                    <div><span className="text-[10px] text-[#1e3d3a]/40">Total cost</span><div className="tabular-nums text-sm">{moneyProp(j.total_cost || 0)}</div></div>
                    <div className="text-right"><span className="text-[10px] text-[#1e3d3a]/40">Profit</span><div className={`tabular-nums text-sm font-semibold ${(j.profit || 0) >= 0 ? "text-[#2f7d5b]" : "text-[#b23a48]"}`}>{moneyProp(j.profit || 0)} <span className="text-[10px] font-normal">({j.margin || 0}%)</span></div></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        />
      )}

      {/* ONBOARDING (derived from employees) */}
      {section === "onboarding" && (() => {
        const emps = data.data.employees || [];
        const fully = emps.filter((e) => e.connect_payouts_enabled && e.training_done && e.paperwork_done).length;
        const statusChip = (e) => {
          const s = e.connect_payouts_enabled ? "ready" : e.stripe_connect_account_id ? "pending" : "none";
          const map = { ready: ["Ready to pay", "bg-[#2f7d5b]/15 text-[#2f7d5b]"], pending: ["Onboarding…", "bg-[#c9a961]/20 text-[#8a6d2f]"], none: ["Not started", "bg-[#1e3d3a]/8 text-[#1e3d3a]/55"] };
          return map[s];
        };
        return (
          <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 lg:p-6" data-testid="ops-onboarding">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-[#1e3d3a]"><BadgeCheck className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">Employee onboarding</h3></div>
              <span className="text-sm text-[#1e3d3a]/60" data-testid="ops-onboarding-count">{fully} of {emps.length} fully onboarded</span>
            </div>
            {emps.length === 0 ? (
              <p className="text-sm text-[#1e3d3a]/50 py-8 text-center">Add employees first — then track their banking, training and paperwork here.</p>
            ) : (
              <div className="space-y-3">
                {emps.map((e) => {
                  const [label, cls] = statusChip(e);
                  return (
                    <div key={e.id} data-testid={`ops-onboard-${e.id}`} className="rounded-xl border border-[#1e3d3a]/10 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-[#1e3d3a]">{e.name || "Employee"}</div>
                          <div className="text-[11px] text-[#1e3d3a]/50">{e.email || "No email on file"}{e.bank_last4 ? ` · bank ••${e.bank_last4}` : ""}</div>
                        </div>
                        <span className={`rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wide ${cls}`}>{label}</span>
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div className="flex items-center gap-2 text-sm">
                          {e.connect_payouts_enabled ? <CheckSquare className="h-4 w-4 text-[#2f7d5b]" /> : <Square className="h-4 w-4 text-[#1e3d3a]/30" />}
                          <span className="text-[#1e3d3a]/75"><ShieldCheck className="inline h-3.5 w-3.5 -mt-0.5 text-[#c9a961]" /> Banking set up</span>
                        </div>
                        <button data-testid={`ops-onboard-train-${e.id}`} onClick={() => empOnboard(e.id, "training_done", !e.training_done)} className="flex items-center gap-2 text-sm text-left hover:opacity-80">
                          {e.training_done ? <CheckSquare className="h-4 w-4 text-[#2f7d5b]" /> : <Square className="h-4 w-4 text-[#1e3d3a]/30" />}
                          <span className="text-[#1e3d3a]/75"><GraduationCap className="inline h-3.5 w-3.5 -mt-0.5 text-[#c9a961]" /> Training completed</span>
                        </button>
                        <button data-testid={`ops-onboard-paper-${e.id}`} onClick={() => empOnboard(e.id, "paperwork_done", !e.paperwork_done)} className="flex items-center gap-2 text-sm text-left hover:opacity-80">
                          {e.paperwork_done ? <CheckSquare className="h-4 w-4 text-[#2f7d5b]" /> : <Square className="h-4 w-4 text-[#1e3d3a]/30" />}
                          <span className="text-[#1e3d3a]/75"><Pencil className="inline h-3.5 w-3.5 -mt-0.5 text-[#c9a961]" /> Paperwork signed</span>
                        </button>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button data-testid={`ops-onboard-connect-${e.id}`} onClick={() => empConnect(e.id)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a534d]">
                          <ExternalLink className="h-3.5 w-3.5" /> {e.stripe_connect_account_id ? "Resend banking link" : "Set up banking (Stripe)"}
                        </button>
                        {e.stripe_connect_account_id && (
                          <button data-testid={`ops-onboard-refresh-${e.id}`} onClick={() => empRefresh(e.id)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-xs text-[#1e3d3a]/75 hover:border-[#c9a961]">
                            <RefreshCw className="h-3.5 w-3.5" /> Refresh
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button data-testid="ops-stripe-check" onClick={stripeCheck} disabled={stripeChk?.loading}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-xs text-[#1e3d3a]/75 hover:border-[#c9a961] disabled:opacity-50">
                {stripeChk?.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 text-[#c9a961]" />} Check Stripe setup
              </button>
              {stripeChk && !stripeChk.loading && (
                <span data-testid="ops-stripe-check-result" className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] ${stripeChk.ok ? "bg-[#2f7d5b]/15 text-[#2f7d5b]" : "bg-[#b23a48]/12 text-[#b23a48]"}`}>
                  {stripeChk.ok ? <CheckSquare className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />} {stripeChk.message}
                </span>
              )}
            </div>
            {stripeChk && !stripeChk.loading && !stripeChk.ok && (stripeChk.v1_url || stripeChk.connect_url) && (
              <p className="mt-2 text-[11px] text-[#1e3d3a]/60" data-testid="ops-stripe-check-link">
                {stripeChk.connect_enabled === false
                  ? <>Connect must be enabled on your <strong>sandbox</strong> account (not your personal Stripe login). Claim the sandbox under <strong>Manage → Payments</strong>, enable <a href={stripeChk.connect_url} target="_blank" rel="noopener noreferrer" className="text-[#c9a961] underline">Connect</a> in that account, then re-check.</>
                  : <>This toggle lives on your <strong>sandbox</strong> account (not your personal Stripe login). Claim the sandbox under <strong>Manage → Payments</strong>, then in that account go to <a href={stripeChk.v1_url} target="_blank" rel="noopener noreferrer" className="text-[#c9a961] underline">Settings → Features → “Accounts v1 support”</a> and turn it on, then re-check.</>}
              </p>
            )}
            <p className="mt-3 text-[10px] text-[#1e3d3a]/45">Employees enter their own bank details securely via Stripe — you only ever see the last 4 digits. We send the gross amount; the net take-home estimate (FICA + federal + your state %) shows on each pay run so you can plan withholdings. Payroll tax remittance remains your responsibility.</p>
          </div>
        );
      })()}

      {/* PAYROLL SCHEDULE (auto-draft cadence) */}
      {section === "payroll" && sched && (
        <div className="mb-4 rounded-2xl border border-[#1e3d3a]/12 bg-white p-4 lg:p-5" data-testid="ops-payroll-schedule">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[#1e3d3a]"><CalendarClock className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">Payroll schedule</h3></div>
            <label className="inline-flex items-center gap-2 text-sm text-[#1e3d3a]/75 cursor-pointer" data-testid="ops-sched-toggle">
              <input type="checkbox" checked={!!sched.enabled} onChange={(e) => saveSchedule({ enabled: e.target.checked })} className="accent-[#2f7d5b] h-4 w-4" />
              Auto-draft pay runs
            </label>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="min-w-0">
              <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Cadence</label>
              <select data-testid="ops-sched-cadence" value={sched.cadence} onChange={(e) => setSched({ ...sched, cadence: e.target.value })} className={inputCls}>
                {(sched.cadence_options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="min-w-0">
              <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">First pay date</label>
              <input data-testid="ops-sched-anchor" type="date" value={sched.anchor || ""} onChange={(e) => setSched({ ...sched, anchor: e.target.value })} className={inputCls} />
            </div>
            <div className="flex items-end flex-wrap gap-2 min-w-0">
              <button data-testid="ops-sched-save" onClick={() => saveSchedule({})} disabled={schedBusy} className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3 py-2 text-xs font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
                {schedBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
              <button data-testid="ops-sched-run" onClick={runSchedule} disabled={schedBusy || !sched.enabled} className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-2 text-xs text-[#1e3d3a]/75 hover:border-[#c9a961] disabled:opacity-40">
                <Sparkles className="h-3.5 w-3.5 text-[#c9a961]" /> Generate drafts now
              </button>
            </div>
          </div>
          <p className="mt-2 text-[10px] text-[#1e3d3a]/45">
            {sched.enabled
              ? `Draft pay runs are created automatically for every active employee on your cadence${sched.next_run_date ? ` · next: ${sched.next_run_date}` : ""} — review, then approve or pay each one.`
              : "Turn on auto-draft to have pay runs created for every active employee on your cadence, ready to review."}
          </p>
          {sched.enabled && (sched.upcoming || []).length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="ops-sched-upcoming">
              <span className="text-[10px] uppercase tracking-wide text-[#1e3d3a]/40">Upcoming</span>
              {sched.upcoming.map((d, i) => (
                <span key={d} className={`rounded-full px-2 py-0.5 text-[10px] ${i === 0 ? "bg-[#1e7d5a]/12 text-[#1e7d5a] font-medium" : "bg-[#1e3d3a]/6 text-[#1e3d3a]/60"}`}>
                  {new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* GENERIC TABLE ENTITIES */}
      {["employees", "payroll", "overhead", "insurance", "equipment", "revenue", "taxes"].includes(section) && (
        <EntitySection
          entity={section} spec={entitiesByKey[section]} rows={data.data[section]}
          onAdd={() => openAdd(section)} onImport={() => triggerImport(section)} busy={busy}
          render={(rows) => rows.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="text-[#1e3d3a]/45 border-b border-[#1e3d3a]/10 text-xs">
                  {entitiesByKey[section].fields.map((f) => (
                    <th key={f.key} className={`py-2 font-medium ${f.type === "num" ? "text-right" : "text-left"}`}>{f.label}</th>
                  ))}
                  <th className="py-2"></th>
                </tr></thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} data-testid={`ops-row-${section}-${row.id}`} className="border-b border-[#1e3d3a]/6">
                      {entitiesByKey[section].fields.map((f) => (
                        <td key={f.key} className={`py-1.5 ${f.type === "num" ? "text-right tabular-nums text-[#1e3d3a]" : "text-[#1e3d3a]/75"}`}>
                          {renderCell(f, row, () => togglePaid(section, row))}
                          {f.type === "str" && f === entitiesByKey[section].fields.find((x) => x.type === "str") && row.added_by === "elevare" && <span className="ml-1.5 text-[9px] uppercase text-[#c9a961]">Elevare</span>}
                          {f.type === "str" && f === entitiesByKey[section].fields.find((x) => x.type === "str") && row.source === "upload" && <span className="ml-1.5 text-[9px] uppercase text-[#c9a961]">AI</span>}
                        </td>
                      ))}
                      <td className="py-1.5 text-right whitespace-nowrap">
                        {section === "payroll" && row.draft && (
                          <span data-testid={`ops-draft-${row.id}`} className="mr-1 rounded-full bg-[#c9a961]/20 px-2 py-0.5 text-[9px] uppercase tracking-wide text-[#8a6d2f]">Draft</span>
                        )}
                        {section === "payroll" && (() => {
                          const st = row.payout_status || (row.paid ? "paid" : "unpaid");
                          const chip = { in_transit: ["In transit", "bg-[#c9a961]/20 text-[#8a6d2f]"], paid: ["Paid ✓", "bg-[#2f7d5b]/15 text-[#2f7d5b]"], failed: ["Failed", "bg-[#b23a48]/12 text-[#b23a48]"], returned: ["Returned", "bg-[#b23a48]/12 text-[#b23a48]"] };
                          if (st === "unpaid" || st === "failed" || st === "returned") {
                            return (
                              <span className="mr-1 inline-flex items-center gap-1.5" data-testid={`ops-payout-${row.id}`}>
                                {(st === "failed" || st === "returned") && <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${chip[st][1]}`}>{chip[st][0]}</span>}
                                <button data-testid={`ops-pay-${row.id}`} onClick={() => payRun(row.id)} disabled={busy}
                                  className="inline-flex items-center gap-1 rounded-full bg-[#2f7d5b] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white hover:bg-[#276a4d] disabled:opacity-50">
                                  <Send className="h-3 w-3" /> {st === "unpaid" ? "Pay" : "Retry"}
                                </button>
                              </span>
                            );
                          }
                          return (
                            <span className="mr-1 inline-flex items-center gap-1.5" data-testid={`ops-payout-${row.id}`}>
                              <span data-testid={`ops-payout-status-${row.id}`} className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${chip[st][1]}`}>{chip[st][0]}</span>
                              {st === "in_transit" && (
                                <button data-testid={`ops-payout-check-${row.id}`} onClick={() => checkPayout(row.id)} disabled={busy}
                                  className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]" title="Check payout status"><RefreshCw className="h-3.5 w-3.5" /></button>
                              )}
                            </span>
                          );
                        })()}
                        {section === "payroll" && row.draft && (
                          <button data-testid={`ops-approve-${row.id}`} onClick={() => approveDraft(row.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#2f7d5b]" title="Approve draft"><Check className="h-3.5 w-3.5" /></button>
                        )}
                        {section === "payroll" && (
                          <button data-testid={`ops-stub-${row.id}`} onClick={() => downloadStub(row.id, (data.data.employees || []).find((e) => e.id === row.employee_id)?.name)} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]" title="Download pay stub (PDF)"><FileText className="h-3.5 w-3.5" /></button>
                        )}
                        <button data-testid={`ops-move-${section}-${row.id}`} onClick={() => setMoveFor({ entity: section, row })} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]" title="Move to another tab"><ArrowRightLeft className="h-3.5 w-3.5" /></button>
                        <button data-testid={`ops-edit-${section}-${row.id}`} onClick={() => openEdit(section, row)} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`ops-del-${section}-${row.id}`} onClick={() => del(section, row.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#b23a48]"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-sm text-[#1e3d3a]/50 py-8 text-center">Nothing here yet. Add a record, or upload a CSV/photo to auto-fill with AI.</p>}
        />
      )}

      {/* FORM MODAL */}
      {form && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-4" data-testid="ops-modal" onClick={() => setForm(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl text-[#1e3d3a]">{form.id ? "Edit" : "Add"} {entitiesByKey[form.entity].singular.toLowerCase()}</h3>
              <button data-testid="ops-modal-close" onClick={() => setForm(null)} className="text-[#1e3d3a]/40 hover:text-[#1e3d3a]"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {entitiesByKey[form.entity].fields.map((f) => (
                <div key={f.key} className={"min-w-0 " + (f.type === "str" || f.type === "bool" ? "col-span-2" : "")}>
                  <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">{f.label}</label>
                  <FieldInput f={f} value={form.values[f.key]} onChange={(v) => setForm((s) => ({ ...s, values: { ...s.values, [f.key]: v } }))} refOptions={refOptions} />
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setForm(null)} className="rounded-full border border-[#1e3d3a]/20 px-4 py-2 text-sm text-[#1e3d3a]/70">Cancel</button>
              <button data-testid="ops-modal-save" disabled={busy} onClick={save} className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
            </div>
          </div>
        </div>
      )}

      {/* MOVE MODAL — reclassify a record into another tab */}
      {moveFor && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-4" data-testid="ops-move-modal" onClick={() => setMoveFor(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-head text-xl text-[#1e3d3a]">Move to another tab</h3>
              <button data-testid="ops-move-close" onClick={() => setMoveFor(null)} className="text-[#1e3d3a]/40 hover:text-[#1e3d3a]"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-[#1e3d3a]/60 mb-4">Move <span className="font-medium text-[#1e3d3a]">{displayName(moveFor.entity, moveFor.row)}</span> out of <span className="font-medium">{entitiesByKey[moveFor.entity]?.label}</span> into:</p>
            <div className="grid grid-cols-2 gap-2">
              {(data.entities || []).filter((e) => e.key !== moveFor.entity).map((e) => {
                const Icon = ICONS[e.icon] || Users;
                return (
                  <button key={e.key} data-testid={`ops-move-to-${e.key}`} onClick={() => move(moveFor.entity, moveFor.row.id, e.key)}
                    className="inline-flex items-center gap-2 rounded-xl border border-[#1e3d3a]/12 px-3 py-2.5 text-sm text-[#1e3d3a]/80 hover:border-[#c9a961] hover:bg-[#c9a961]/8 transition-colors text-left">
                    <Icon className="h-4 w-4 text-[#c9a961] shrink-0" /> {e.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {progressJob && <JobProgress job={progressJob} onClose={() => setProgressJob(null)} />}

      {/* CPA share modal — email the Tax Prep Pack to an accountant */}
      {cpaModal && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-4" data-testid="ops-cpa-modal" onClick={() => setCpaModal(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-head text-xl text-[#1e3d3a]">Email tax pack to your CPA</h3>
              <button data-testid="ops-cpa-close" onClick={() => setCpaModal(false)} className="text-[#1e3d3a]/40 hover:text-[#1e3d3a]"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-[#1e3d3a]/60 mb-4">We'll email your accountant a secure link that always downloads your latest tax prep pack.</p>
            <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Accountant email</label>
            <input data-testid="ops-cpa-email" type="email" value={cpaEmail} onChange={(e) => setCpaEmail(e.target.value)}
              placeholder="cpa@firm.com" className={inputCls + " mb-3"} />
            <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Accountant name (optional)</label>
            <input data-testid="ops-cpa-name" value={cpaName} onChange={(e) => setCpaName(e.target.value)}
              placeholder="Jane Doe, CPA" className={inputCls} />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setCpaModal(false)} className="rounded-full border border-[#1e3d3a]/20 px-4 py-2 text-sm text-[#1e3d3a]/70">Cancel</button>
              <button data-testid="ops-cpa-send" disabled={cpaBusy} onClick={sendToCpa}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50">
                {cpaBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function renderCell(f, row, onTogglePaid) {
  const v = row[f.key];
  if (f.type === "bool") {
    return (
      <button data-testid={`ops-paid-${row.id}`} onClick={f.key === "paid" ? onTogglePaid : undefined}
        className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${v ? "bg-[#2f7d5b]/15 text-[#2f7d5b]" : "bg-[#c9a961]/18 text-[#8a6d2f]"}`}>
        {v ? "Yes" : "No"}
      </button>
    );
  }
  if (f.type === "ref") {
    const nameKey = (f.ref === "employees" ? "employee" : "job") + "_name";
    return <span className="text-[#1e3d3a]/70">{row[nameKey] || "—"}</span>;
  }
  if (f.type === "num") {
    const txt = `$${Math.round(v || 0).toLocaleString()}`;
    if (f.key === "gross_pay" && row.net_pay) {
      return (
        <span>
          {txt}
          <span className="block text-[9px] font-normal text-[#1e3d3a]/45" title={`FICA $${Math.round(row.net_pay.fica)} · Fed $${Math.round(row.net_pay.federal)} · State $${Math.round(row.net_pay.state)} (${row.net_pay.state_pct}%)`}>
            net ≈ ${Math.round(row.net_pay.net).toLocaleString()}
          </span>
        </span>
      );
    }
    if (f.key === "state_tax_pct") return v ? `${v}%` : "—";
    return txt;
  }
  return v || "—";
}

function FieldInput({ f, value, onChange, refOptions }) {
  if (f.type === "num") return <input data-testid={`ops-field-${f.key}`} type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  if (f.type === "date") return <input data-testid={`ops-field-${f.key}`} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  if (f.type === "bool") return (
    <label className="inline-flex items-center gap-2 text-sm text-[#1e3d3a]/75"><input data-testid={`ops-field-${f.key}`} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#c9a961]" /> {f.label}</label>
  );
  if (f.type === "select") return (
    <select data-testid={`ops-field-${f.key}`} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
  );
  if (f.type === "ref") {
    const opts = refOptions(f.ref);
    return (
      <select data-testid={`ops-field-${f.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        <option value="">{f.optional ? "— none —" : "— select —"}</option>
        {opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    );
  }
  return <input data-testid={`ops-field-${f.key}`} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
}

function EntitySection({ entity, spec, rows, onAdd, onImport, busy, render }) {
  const Icon = ICONS[spec?.icon] || Users;
  return (
    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 lg:p-6" data-testid={`ops-section-${entity}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-[#1e3d3a]"><Icon className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">{spec?.label}</h3></div>
        <div className="flex items-center gap-2">
          <button data-testid={`ops-import-${entity}`} disabled={busy} onClick={onImport} className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 px-3 py-1.5 text-xs text-[#1e3d3a]/75 hover:border-[#c9a961] disabled:opacity-50 transition-colors">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Import
          </button>
          <button data-testid={`ops-add-${entity}`} onClick={onAdd} className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2a534d] transition-colors"><Plus className="h-3.5 w-3.5" /> Add</button>
        </div>
      </div>
      {render(rows || [])}
    </div>
  );
}

function LaborTable({ title, rows, extra = [], money }) {
  const all = [...(rows || []), ...extra];
  const total = all.reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5" data-testid={`ops-labor-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 text-[#1e3d3a] mb-3"><HardHat className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">{title}</h3></div>
      {all.length ? (
        <ul className="space-y-2">
          {all.map((r, i) => {
            const pct = total ? Math.round((r.amount / total) * 100) : 0;
            return (
              <li key={i}>
                <div className="flex items-center justify-between text-sm"><span className="text-[#1e3d3a]/80">{r.name}</span><span className="tabular-nums text-[#1e3d3a]">{money(r.amount)}</span></div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-[#1e3d3a]/8 overflow-hidden"><div className="h-full rounded-full bg-[#c9a961]" style={{ width: `${pct}%` }} /></div>
              </li>
            );
          })}
          <li className="pt-2 mt-2 border-t border-[#1e3d3a]/10 flex items-center justify-between text-sm font-semibold"><span>Total labor</span><span className="tabular-nums">{money(total)}</span></li>
        </ul>
      ) : <p className="text-sm text-[#1e3d3a]/50 py-6 text-center">Record payroll to see labor cost breakdowns.</p>}
    </div>
  );
}
